"use client";

import { useRef, useState } from "react";
import classNames from "@/helpers/classNames";
import { minAmountError, sanitiseAmount } from "@/helpers/amount";
import styles from "./styles.module.scss";

export interface DonationAmountInputProps {
  suggestedAmounts: number[];
  minAmount: number;
  value: number | null;
  onChange: (amount: number | null) => void;
  className?: string;
}

const formatDollars = (amount: number) => `$${amount % 1 === 0 ? amount : amount.toFixed(2)}`;

const DonationAmountInput = ({
  suggestedAmounts,
  minAmount,
  value,
  onChange,
  className,
}: DonationAmountInputProps) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customInputValue, setCustomInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSuggestedClick = (amount: number) => {
    setIsCustom(false);
    setCustomInputValue("");
    setError(null);
    onChange(amount);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    onChange(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const validateAndSetCustomAmount = (raw: string) => {
    const trimmed = sanitiseAmount(raw);
    const parsed = Number.parseFloat(trimmed);

    if (!trimmed || isNaN(parsed)) {
      setError(null);
      onChange(null);
      return;
    }

    if (parsed < minAmount) {
      setError(minAmountError(minAmount));
      onChange(null);
      return;
    }

    setError(null);
    onChange(parsed);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cleaned as it's typed, matching the other amount fields. Previously the
    // raw value was kept until blur, so letters stayed visible in the field
    // until the supporter clicked away.
    setCustomInputValue(sanitiseAmount(e.target.value));

    if (error) {
      setError(null);
    }
  };

  const handleInputBlur = () => {
    validateAndSetCustomAmount(customInputValue);

    const trimmed = sanitiseAmount(customInputValue);
    const parsed = Number.parseFloat(trimmed);
    if (!isNaN(parsed) && parsed > 0) {
      setCustomInputValue(parsed % 1 === 0 ? parsed.toString() : parsed.toFixed(2));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validateAndSetCustomAmount(customInputValue);
      inputRef.current?.blur();
    }
  };

  const isSuggestedSelected = (amount: number) => !isCustom && value === amount;

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.amounts}>
        {suggestedAmounts.map((amount) => (
          <button
            key={amount}
            type="button"
            className={classNames(styles.amountButton, {
              [styles.amountButtonSelected]: isSuggestedSelected(amount),
            })}
            onClick={() => handleSuggestedClick(amount)}
            aria-pressed={isSuggestedSelected(amount)}
          >
            {formatDollars(amount)}
          </button>
        ))}
        <button
          type="button"
          className={classNames(styles.amountButton, {
            [styles.amountButtonSelected]: isCustom,
          })}
          onClick={handleCustomClick}
          aria-pressed={isCustom}
        >
          Other
        </button>
      </div>

      {isCustom && (
        <div className={styles.customInput}>
          <div className={styles.inputWrapper}>
            <span className={styles.prefix}>$</span>
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={customInputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              aria-label="Custom donation amount"
              aria-invalid={!!error}
              aria-describedby={error ? "donation-error" : undefined}
              className={classNames(styles.input, { [styles.inputError]: !!error })}
            />
          </div>
          {error && (
            <p id="donation-error" className={styles.error} aria-live="polite" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DonationAmountInput;
